import React, { useState } from 'react';
import { Search, Activity, User, Heart, TrendingUp, FileText, CheckCircle, X, ClipboardList } from 'lucide-react';

const PatientRecords = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    // Vital Signs
    weight: '',
    height: '',
    bloodPressure: '',
    temperature: '',
    heartRate: '',
    // Maternal Health
    pregnancyStatus: '',
    gestationalAge: '',
    lastMenstrualPeriod: '',
    expectedDueDate: '',
    fundalHeight: '',
    // Medical Information
    bloodGroup: '',
    allergies: '',
    currentMedications: '',
    medicalHistory: '',
    notes: '',
  });

  const [success, setSuccess] = useState(false);

  // Mock registered patients - in real app, this would come from API
  const [registeredPatients] = useState([
    { id: 'patient1', name: 'Jane Doe', email: 'jane.doe@email.com', phone: '+234 801 234 5678', hasRecords: false, condition: 'Pregnant - 24 weeks' },
    { id: 'patient2', name: 'Mary Johnson', email: 'mary.j@email.com', phone: '+234 802 345 6789', hasRecords: true, condition: 'Postnatal Care' },
    { id: 'patient3', name: 'Sarah Williams', email: 'sarah.w@email.com', phone: '+234 803 456 7890', hasRecords: false, condition: 'Pregnant - 12 weeks' },
    { id: 'patient4', name: 'Emily Brown', email: 'emily.b@email.com', phone: '+234 804 567 8901', hasRecords: false, condition: 'Child Health' },
  ]);

  const filteredPatients = registeredPatients.filter(patient => 
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.phone.includes(searchTerm)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      alert('Please select a patient first');
      return;
    }
    
    console.log('Saving patient records for:', selectedPatient);
    console.log('Medical data:', formData);
    
    // In production, this would save medical data to the patient's profile
    setSuccess(true);
    
    // Reset form after 2 seconds
    setTimeout(() => {
      setSuccess(false);
      setSelectedPatient(null);
      setSearchTerm('');
      setFormData({
        weight: '',
        height: '',
        bloodPressure: '',
        temperature: '',
        heartRate: '',
        pregnancyStatus: '',
        gestationalAge: '',
        lastMenstrualPeriod: '',
        expectedDueDate: '',
        fundalHeight: '',
        bloodGroup: '',
        allergies: '',
        currentMedications: '',
        medicalHistory: '',
        notes: '',
      });
    }, 2000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
  };

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                Patient Records & Vitals
              </h1>
              <p className="text-gray-600 mt-1">Search for a patient and add or update their medical records</p>
            </div>
          </div>
        </div>

        {success && (
          <div className="mb-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm animate-in fade-in slide-in-from-top-4">
            <div className="p-2 bg-emerald-500 rounded-xl shadow-lg">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <p className="text-emerald-800 font-medium">Patient records saved successfully!</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Patient Search/Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-emerald-600" />
                Search Patient
              </h2>

              {/* Search Input */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or phone"
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Selected Patient */}
              {selectedPatient && (
                <div className="mb-4 p-4 bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-xl">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedPatient.name}</h3>
                        <p className="text-sm text-gray-600">{selectedPatient.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedPatient(null)}
                      className="p-1 hover:bg-emerald-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">{selectedPatient.phone}</p>
                  <p className="text-xs text-emerald-700 font-medium mt-1">{selectedPatient.condition}</p>
                  {selectedPatient.hasRecords && (
                    <div className="mt-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-lg inline-block">
                      Has existing records
                    </div>
                  )}
                </div>
              )}

              {/* Patient List */}
              {searchTerm && !selectedPatient && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map(patient => (
                      <button
                        key={patient.id}
                        onClick={() => handleSelectPatient(patient)}
                        className="w-full p-3 text-left border border-gray-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                            <User className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{patient.name}</p>
                            <p className="text-xs text-gray-600 truncate">{patient.email}</p>
                            <p className="text-xs text-emerald-600 font-medium">{patient.condition}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">No patients found</p>
                  )}
                </div>
              )}

              {!searchTerm && !selectedPatient && (
                <div className="text-center py-8 text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Start typing to search for a patient</p>
                </div>
              )}

              {/* Guidelines */}
              <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-5 h-5 text-blue-700" />
                  <h3 className="font-semibold text-blue-900">Guidelines</h3>
                </div>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Ensure all measurements are accurate</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Use calibrated equipment</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Note any unusual observations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Medical Records Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Vital Signs */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                  Vital Signs
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="text"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 65"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Height (cm)
                    </label>
                    <input
                      type="text"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 165"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Blood Pressure (mmHg)
                    </label>
                    <input
                      type="text"
                      name="bloodPressure"
                      value={formData.bloodPressure}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 120/80"
                    />
                    <p className="text-xs text-gray-500 mt-1">Normal: 90/60 - 120/80 mmHg</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Temperature (°C)
                    </label>
                    <input
                      type="text"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 36.8"
                    />
                    <p className="text-xs text-gray-500 mt-1">Normal: 36.5 - 37.5 °C</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Heart Rate (bpm)
                    </label>
                    <input
                      type="text"
                      name="heartRate"
                      value={formData.heartRate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 72"
                    />
                    <p className="text-xs text-gray-500 mt-1">Normal: 60 - 100 bpm</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Blood Group
                    </label>
                    <select
                      name="bloodGroup"
                      value={formData.bloodGroup}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Maternal Health */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-600" />
                  Maternal Health
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pregnancy Status
                    </label>
                    <select
                      name="pregnancyStatus"
                      value={formData.pregnancyStatus}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select status</option>
                      <option value="pregnant">Pregnant</option>
                      <option value="postnatal">Postnatal</option>
                      <option value="not-pregnant">Not Pregnant</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Gestational Age (weeks)
                    </label>
                    <input
                      type="text"
                      name="gestationalAge"
                      value={formData.gestationalAge}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 24"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Menstrual Period
                    </label>
                    <input
                      type="date"
                      name="lastMenstrualPeriod"
                      value={formData.lastMenstrualPeriod}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Expected Due Date
                    </label>
                    <input
                      type="date"
                      name="expectedDueDate"
                      value={formData.expectedDueDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Fundal Height (cm)
                    </label>
                    <input
                      type="text"
                      name="fundalHeight"
                      value={formData.fundalHeight}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., 24"
                    />
                  </div>
                </div>
              </div>

              {/* Medical Information */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  Medical Information
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Allergies
                    </label>
                    <input
                      type="text"
                      name="allergies"
                      value={formData.allergies}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="e.g., Penicillin, Peanuts"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Medications
                    </label>
                    <textarea
                      name="currentMedications"
                      value={formData.currentMedications}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                      placeholder="List current medications..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Medical History
                    </label>
                    <textarea
                      name="medicalHistory"
                      value={formData.medicalHistory}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                      placeholder="Past medical conditions, surgeries, etc..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                      placeholder="Any other relevant information..."
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={!selectedPatient}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white py-4 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 focus:ring-4 focus:ring-emerald-500/50 transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  Save Patient Records
                </button>
                {selectedPatient && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPatient(null);
                      setFormData({
                        weight: '',
                        height: '',
                        bloodPressure: '',
                        temperature: '',
                        heartRate: '',
                        pregnancyStatus: '',
                        gestationalAge: '',
                        lastMenstrualPeriod: '',
                        expectedDueDate: '',
                        fundalHeight: '',
                        bloodGroup: '',
                        allergies: '',
                        currentMedications: '',
                        medicalHistory: '',
                        notes: '',
                      });
                    }}
                    className="px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientRecords;
